/**
 * CR277: Loyalty Program Lookup Utility
 * 
 * Provides client-side mapping from points program display names to program IDs
 * and conversion rates. This enables cash value calculation for POINTS deals
 * without requiring an additional API call.
 * 
 * The lookup uses a case-insensitive match against known program names.
 * If no match is found, returns null to trigger the "Cash value not available" warning.
 */

export interface LoyaltyProgramInfo {
  programId: string;
  programName: string;
  conversionRate: number; // CAD per point
}

/**
 * Static lookup table for Canadian loyalty programs
 * Matches the database values in public.loyalty_programs
 * 
 * Note: This is a client-side cache. For server-side normalization,
 * use supabase/functions/_shared/loyaltyLookup.ts
 */
const LOYALTY_PROGRAMS: LoyaltyProgramInfo[] = [
  { programId: "pc-optimum", programName: "PC Optimum", conversionRate: 0.001 },
  { programId: "shoppers-optimum", programName: "PC Optimum", conversionRate: 0.001 }, // Alternate for Shoppers
  { programId: "scene-plus", programName: "Scene+", conversionRate: 0.01 },
  { programId: "petro-points", programName: "Petro-Points", conversionRate: 0.001 },
  { programId: "triangle-rewards", programName: "Triangle Rewards", conversionRate: 0.01 },
  { programId: "sport-chek-triangle", programName: "Triangle Rewards", conversionRate: 0.01 },
  { programId: "marks-triangle", programName: "Triangle Rewards", conversionRate: 0.01 },
  { programId: "airmiles", programName: "AIR MILES", conversionRate: 0.105 },
  { programId: "rbc-avion", programName: "RBC Avion", conversionRate: 0.01 },
  { programId: "aeroplan", programName: "Aeroplan", conversionRate: 0.012 },
];

/**
 * Map of normalized program name patterns to canonical program IDs
 * Used for fuzzy matching when display names vary
 */
const PROGRAM_NAME_ALIASES: Record<string, string> = {
  // PC Optimum variants
  "pc optimum": "pc-optimum",
  "pc_optimum": "pc-optimum",
  "pcoptimum": "pc-optimum",
  "optimum": "pc-optimum",
  "shoppers optimum": "shoppers-optimum",
  "shoppers points": "shoppers-optimum",
  
  // Scene+ variants
  "scene+": "scene-plus",
  "scene plus": "scene-plus",
  "sceneplus": "scene-plus",
  "scene": "scene-plus",
  
  // Petro-Points variants
  "petro-points": "petro-points",
  "petro points": "petro-points",
  "petropoints": "petro-points",
  
  // Triangle Rewards variants
  "triangle rewards": "triangle-rewards",
  "triangle": "triangle-rewards",
  "ct money": "triangle-rewards",
  "canadian tire money": "triangle-rewards",
  "canadian tire": "triangle-rewards",
  
  // Air Miles variants
  "air miles": "airmiles",
  "airmiles": "airmiles",
  
  // Aeroplan variants
  "aeroplan": "aeroplan",
  "aero plan": "aeroplan",
  
  // RBC Avion variants
  "rbc avion": "rbc-avion",
  "avion": "rbc-avion",
};

/**
 * Default program ID selection for ambiguous matches
 * When multiple program_ids share the same program_name, use this mapping
 * 
 * CR277 R3: Deterministic selection for ambiguous matches
 * - "PC Optimum" → "pc-optimum" (not shoppers-optimum)
 * - "Triangle Rewards" → "triangle-rewards" (not sport-chek or marks variants)
 */
const DEFAULT_PROGRAM_SELECTION: Record<string, string> = {
  "PC Optimum": "pc-optimum",
  "Triangle Rewards": "triangle-rewards",
};

/**
 * Normalize a program name for lookup
 */
function normalizeForLookup(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\w\s]/g, "") // Remove special chars except spaces
    .replace(/\s+/g, " ")    // Normalize whitespace
    .trim();
}

/**
 * Look up loyalty program by display name (case-insensitive)
 * 
 * @param programName - The display name (e.g., "PC Optimum", "Scene+")
 * @returns LoyaltyProgramInfo or null if not found
 */
export function lookupByProgramName(programName: string | null | undefined): LoyaltyProgramInfo | null {
  if (!programName) return null;
  
  const normalized = normalizeForLookup(programName);
  
  // Try alias mapping first
  const aliasedId = PROGRAM_NAME_ALIASES[normalized];
  if (aliasedId) {
    const program = LOYALTY_PROGRAMS.find(p => p.programId === aliasedId);
    if (program) return program;
  }
  
  // Try direct name match (case-insensitive)
  const directMatch = LOYALTY_PROGRAMS.find(
    p => normalizeForLookup(p.programName) === normalized
  );
  if (directMatch) {
    // Apply default selection for ambiguous matches
    const defaultId = DEFAULT_PROGRAM_SELECTION[directMatch.programName];
    if (defaultId && directMatch.programId !== defaultId) {
      // Return the default program instead
      const defaultProgram = LOYALTY_PROGRAMS.find(p => p.programId === defaultId);
      if (defaultProgram) return defaultProgram;
    }
    return directMatch;
  }
  
  // Try partial match (e.g., "Optimum" matches "PC Optimum")
  for (const [alias, programId] of Object.entries(PROGRAM_NAME_ALIASES)) {
    if (normalized.includes(alias) || alias.includes(normalized)) {
      const program = LOYALTY_PROGRAMS.find(p => p.programId === programId);
      if (program) return program;
    }
  }
  
  return null;
}

/**
 * Look up loyalty program by program ID
 * 
 * @param programId - The canonical program ID (e.g., "pc-optimum")
 * @returns LoyaltyProgramInfo or null if not found
 */
export function lookupByProgramId(programId: string | null | undefined): LoyaltyProgramInfo | null {
  if (!programId) return null;
  
  return LOYALTY_PROGRAMS.find(p => p.programId === programId) || null;
}

/**
 * Calculate cash value from points amount and program
 * 
 * @param pointsAmount - Number of points
 * @param programNameOrId - Either program name or program ID
 * @returns Object with cashValue, rate, and programId, or null if unable to calculate
 */
export function calculatePointsCashValue(
  pointsAmount: number | null | undefined,
  programNameOrId: string | null | undefined
): { cashValue: number; rate: number; programId: string; programName: string } | null {
  if (!pointsAmount || pointsAmount <= 0 || !programNameOrId) return null;
  
  // Try as program ID first, then as program name
  let program = lookupByProgramId(programNameOrId);
  if (!program) {
    program = lookupByProgramName(programNameOrId);
  }
  
  if (!program || program.conversionRate <= 0) return null;
  
  return {
    cashValue: pointsAmount * program.conversionRate,
    rate: program.conversionRate,
    programId: program.programId,
    programName: program.programName,
  };
}

/**
 * Get all available loyalty programs (for UI dropdowns, etc.)
 */
export function getAllLoyaltyPrograms(): LoyaltyProgramInfo[] {
  // Return unique programs by ID (filter out duplicates like sport-chek-triangle)
  const seen = new Set<string>();
  return LOYALTY_PROGRAMS.filter(p => {
    if (seen.has(p.programId)) return false;
    // Skip variant program IDs in the main list
    if (p.programId !== DEFAULT_PROGRAM_SELECTION[p.programName] && 
        DEFAULT_PROGRAM_SELECTION[p.programName]) {
      return false;
    }
    seen.add(p.programId);
    return true;
  });
}
